import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { useAuthStore } from '../../store';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Wine, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const hydrated = useAuthStore((state) => state.hydrated);
  const activePortal = useAuthStore((state) => state.activePortal);
  const setAuth = useAuthStore((state) => state.setAuth);

  const portal = location.pathname.startsWith('/superadmin')
    ? 'superadmin'
    : location.pathname.startsWith('/admin')
      ? 'admin'
      : 'staff';

  const isAdminLogin = portal === 'admin';
  const isSuperAdminLogin = portal === 'superadmin';

  const canAccessPortal = useCallback((role) => {
    if (portal === 'superadmin') return role === 'superadmin';
    if (portal === 'admin') return ['admin', 'superadmin'].includes(role);
    return ['staff', 'admin', 'superadmin'].includes(role);
  }, [portal]);

  const portalHome = {
    staff: '/staff/dashboard',
    admin: '/admin/dashboard',
    superadmin: '/superadmin/dashboard'
  }[portal];

  useEffect(() => {
    if (!hydrated) return;

    if (token && user && activePortal === portal) {
      if (canAccessPortal(user.role)) {
        navigate(portalHome, { replace: true });
      }
    }
  }, [hydrated, token, user, activePortal, portal, portalHome, canAccessPortal, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authAPI.login(email, password, portal);
      const { token, user } = response.data;

      if (!canAccessPortal(user.role)) {
        toast.error(
          isSuperAdminLogin
            ? 'Accès superadmin requis'
            : isAdminLogin
              ? 'Accès administrateur requis'
              : 'Accès staff requis'
        );
        return;
      }

      setAuth(token, user, portal);

      const from = location.state?.from?.pathname;
      const fromMatchesPortal = from?.startsWith(`/${portal}/`);

      if (fromMatchesPortal) {
        navigate(from, { replace: true });
      } else {
        navigate(portalHome, { replace: true });
      }

      toast.success('Connexion réussie');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Identifiants incorrects');
    } finally {
      setLoading(false);
    }
  };

  if (!hydrated) {
    return (
      <div className="min-h-screen bg-[#111827] flex items-center justify-center">
        <div className="text-sm text-gray-400">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111827] flex items-center justify-center px-4 py-6 sm:p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#7C3AED] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Wine className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope' }}>
            GlassOrder
          </h1>
          <p className="text-gray-400">
            {isSuperAdminLogin
              ? 'Espace Superadmin'
              : isAdminLogin
              ? 'Espace Administrateur'
              : 'Espace Staff'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-5 sm:p-6 shadow-xl">
          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                required
                className="mt-1"
                data-testid="login-email"
              />
            </div>

            <div>
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="password">Mot de passe</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-[#4F46E5] hover:text-[#15803D] hover:underline"
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1"
                data-testid="login-password"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full mt-6 bg-[#111827] hover:bg-gray-800 rounded-full py-5"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Se connecter'}
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
